import { create } from 'zustand'

export type AnnouncementType = 'jackpot' | 'winner' | 'system' | 'promotion'

export interface Announcement {
  id: string
  type: AnnouncementType
  message: string
  priority: number
}

interface AnnouncementState {
  /** Currently visible announcement (null = nothing shown) */
  current: Announcement | null
  /** Queue of pending announcements */
  queue: Announcement[]
  /** Push a new announcement — shows immediately if nothing is displayed, else queues */
  push: (announcement: Omit<Announcement, 'id'>) => void
  /** Dismiss the current announcement and show next in queue */
  dismiss: () => void
  /** Clear everything */
  clearAll: () => void
}

let nextId = 1

export const useAnnouncementStore = create<AnnouncementState>((set, get) => ({
  current: null,
  queue: [],

  push: (announcement) => {
    const item: Announcement = { ...announcement, id: String(nextId++) }
    const { current } = get()
    if (!current) {
      // Show immediately
      set({ current: item })
    } else {
      // Queue it
      set((s) => ({ queue: [...s.queue, item] }))
    }
  },

  dismiss: () => {
    const { queue } = get()
    if (queue.length > 0) {
      // Show next from queue
      const [next, ...rest] = queue
      set({ current: next, queue: rest })
    } else {
      set({ current: null })
    }
  },

  clearAll: () => {
    set({ current: null, queue: [] })
  },
}))

// ─── Demo: fire random announcements periodically ───────────────────
const demoMessages: Omit<Announcement, 'id'>[] = [
  { type: 'jackpot', message: '累積獎池 $12,345.00', priority: 10 },
  { type: 'winner', message: 'Sarah 贏得累積獎金 $17,777！(同花順)', priority: 8 },
  { type: 'winner', message: '阿明 贏得累積獎金 $4,444！(四條 A)', priority: 7 },
  { type: 'system', message: '系統維護將於今晚 03:00 進行', priority: 5 },
  { type: 'promotion', message: '本週雙倍獎金活動進行中！', priority: 6 },
  { type: 'jackpot', message: '累積獎金池突破 $88,888！', priority: 9 },
]

let demoIndex = 0
let demoTimer: ReturnType<typeof setInterval> | null = null

export function startAnnouncementDemo() {
  if (demoTimer) return
  // Fire one after 3s, then every 8-12s
  const fireOne = () => {
    const msg = demoMessages[demoIndex % demoMessages.length]
    demoIndex++
    useAnnouncementStore.getState().push(msg)
  }
  demoTimer = setTimeout(() => {
    fireOne()
    demoTimer = setInterval(fireOne, 8000 + Math.random() * 4000)
  }, 3000) as unknown as ReturnType<typeof setInterval>
}

export function stopAnnouncementDemo() {
  if (demoTimer) {
    clearInterval(demoTimer)
    clearTimeout(demoTimer)
    demoTimer = null
  }
}
